import {
	Form,
	FormItem,
	RadioButtonGroup,
	RadioButtonGroupItem,
	TextInput,
	ValidationError
} from "client/Components";
import { IFormHandler } from "client/Forms";
import {
	ReactElement,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { RevealApiKeyDialog } from "../ApiKeys/RevealApiKeyDialog";
import { AdSyncForm } from "./AdSyncForm";
import { ScimForm } from "./ScimForm";
import {
	UserProvisioningKind,
	UserProvisioningSettingsSaveModel
} from "client/Api/UserProvisioningSettings/userProvisioningSettings";

export type UserProvisioningFormProps = {
	attachable: IFormHandler<UserProvisioningSettingsSaveModel>;
	initialKind: UserProvisioningKind;
	shouldSpecifyComputerName: boolean;
	isAdSyncAvailable: boolean;
	isScimProvisioningAvailable: boolean;
	hasAuthenticationToken: boolean;
	keyToReveal: string | null;
	onKeyToRevealChanged(newToken: string | null): void;
	onRegenerateToken(): void;
};

export const UserProvisioningForm = (props: UserProvisioningFormProps): ReactElement => {
	const {
		attachable,
		initialKind,
		shouldSpecifyComputerName,
		isAdSyncAvailable,
		isScimProvisioningAvailable,
		hasAuthenticationToken,
		keyToReveal,
		onKeyToRevealChanged,
		onRegenerateToken
	} = props;

	const [wasAlertDismissed, setWasAlertDismissed] = useState<boolean>(false);

	const provisioningMethodOptions = useMemo(
		(): RadioButtonGroupItem<UserProvisioningKind>[] => {
			const options: RadioButtonGroupItem<UserProvisioningKind>[] = [
				{
					text: localize("_None"),
					value: UserProvisioningKind.None,
				}
			];

			if (isAdSyncAvailable) {
				options.push({
					text: localize("_AzureActiveDirectoryADSync"),
					value: UserProvisioningKind.AdSync,
				});
			}

			if (isScimProvisioningAvailable) {
				options.push({
					text: localize("_AutomatedUserProvisioning"),
					value: UserProvisioningKind.Scim
				});
			}

			return options;
		},
		[
			isAdSyncAvailable,
			isScimProvisioningAvailable
		]
	);

	return (
		<Form
			onSubmit="NONE"
			hasWidthConstrained
		>
			{shouldSpecifyComputerName && (
				<FormItem label={localize("_ActiveDirectoryServerAddress")}>
					<TextInput
						{...attachable.for("computerName")
							.attachToTextInput()}
					/>
					<ValidationError
						{...attachable
							.for("computerName")
							.attachToValidationError()}
					/>
				</FormItem>
			)}
			<FormItem
				label={localize("_UserProvisioningMethod")}
				isForGroup
			>
				<RadioButtonGroup
					{...attachable
						.for("kind")
						.attachToRadioButtonGroup()}
					options={provisioningMethodOptions}
				/>
				<ValidationError
					{...attachable
						.for("kind")
						.attachToValidationError()}
				/>
			</FormItem>
			{isAdSyncAvailable && (
				attachable
					.when(
						x => x.kind === UserProvisioningKind.AdSync,
						attachable =>
							attachable
								.for("adSync")
								.ensureIsSet(
									adSyncAttachable => {
										const hasAToken: boolean = initialKind === UserProvisioningKind.AdSync
											&& hasAuthenticationToken;
										const shouldRenderTheAlert: boolean = !wasAlertDismissed
											&& !hasAToken;

										return (
											(
												<AdSyncForm
													attachable={adSyncAttachable}
													hasAToken={hasAToken}
													onRegenerateToken={onRegenerateToken}
													shouldRenderTheAlert={shouldRenderTheAlert}
													onAlertDismissed={() => setWasAlertDismissed(true)}
												/>
											)
										);
									}
								)
					)
			)}
			{isScimProvisioningAvailable && (
				attachable
					.when(
						x => x.kind === UserProvisioningKind.Scim,
						attachable =>
							attachable
								.for("scim")
								.ensureIsSet(
									scimAttachable => {
										const hasAToken: boolean = initialKind === UserProvisioningKind.Scim
											&& hasAuthenticationToken;
										const shouldRenderTheAlert: boolean = !wasAlertDismissed
											&& !hasAToken;

										return (
											<ScimForm
												attachable={scimAttachable}
												hasAToken={hasAToken}
												onRegenerateToken={onRegenerateToken}
												shouldRenderTheAlert={shouldRenderTheAlert}
												onAlertDismissed={() => setWasAlertDismissed(true)}
											/>
										);
									}
								)
					)
			)}
			{keyToReveal && (
				<RevealApiKeyDialog
					apiKey={keyToReveal}
					onClose={() => onKeyToRevealChanged(null)}
				/>
			)}
		</Form>
	);
};
UserProvisioningForm.displayName = "UserProvisioningForm";